import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer } from '../hooks/useScrollAnimation';
import SectionHeading from './common/SectionHeading';

const markets = [
  { country: 'Sweden', flag: '🇸🇪' },
  { country: 'Germany', flag: '🇩🇪' },
  { country: 'France', flag: '🇫🇷' },
  { country: 'Spain', flag: '🇪🇸' },
];

const stats = [
  { value: '5', label: 'Languages supported' },
  { value: '10', label: 'Countries available' },
  { value: 'Shared', label: 'Merchant network across all clubs' },
];

const NetworkEffects = () => {
  return (
    <section className="relative py-24 md:py-32 bg-esf-black overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03]"
        aria-hidden="true"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Network Effects"
          headline="The more clubs join, the more everyone earns"
          subheadline="Every new club on the ESF platform adds merchants to the shared network. Your fans benefit from offers across all partner clubs."
        />

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="mt-16 flex flex-col items-center"
        >
          {/*
            Laid out with percentage offsets from a centred origin rather than
            fixed pixel radii, so the diagram scales down cleanly instead of
            being clipped by the section's overflow-hidden on narrow screens.
          */}
          <motion.div
            variants={fadeInUp}
            className="relative w-full max-w-md aspect-square"
            aria-hidden="true"
          >
            {/* Outer ring of merchants */}
            {[...Array(12)].map((_, i) => {
              const rad = ((i * 30) * Math.PI) / 180;
              return (
                <div
                  key={`merchant-${i}`}
                  className="absolute w-2 h-2 rounded-full bg-esf-cyan/40"
                  style={{
                    left: `${50 + Math.cos(rad) * 46}%`,
                    top: `${50 + Math.sin(rad) * 46}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              );
            })}

            {/* Market nodes */}
            {markets.map((market, i) => {
              const rad = (((i * 90) - 45) * Math.PI) / 180;
              return (
                <div
                  key={market.country}
                  className="absolute w-[22%] aspect-square rounded-full bg-esf-card border border-esf-border flex flex-col items-center justify-center gap-0.5"
                  style={{
                    left: `${50 + Math.cos(rad) * 30}%`,
                    top: `${50 + Math.sin(rad) * 30}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <span className="text-base leading-none">{market.flag}</span>
                  <span className="text-[10px] sm:text-xs text-esf-gray-300 font-medium leading-none">
                    {market.country}
                  </span>
                </div>
              );
            })}

            {/* Hub */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[30%] aspect-square rounded-full bg-esf-blue/10 border border-esf-blue/30 flex items-center justify-center"
              style={{ boxShadow: '0 0 60px -10px rgba(59,130,246,0.4)' }}
            >
              <span className="text-xs sm:text-sm font-bold text-esf-blue text-center leading-tight">
                ESF
                <br />
                Network
              </span>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center w-full max-w-3xl"
          >
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-bold text-gradient">{stat.value}</p>
                <p className="text-sm text-esf-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default NetworkEffects;
